import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showConfirm } from './ConfirmDialog';
import { apiGetBandBackup, apiRestoreBand } from '../api';
import { useWriteGate } from '../lib/writeGate';

/**
 * BandBackupSection — download a full backup of a band and restore it from a
 * backup JSON file (Band Settings). Restore is DESTRUCTIVE (replaces ALL the
 * band's data), so it is gated behind a styled confirmation dialog and the
 * server-side canWriteBand check. (2026-08-22)
 */
export default function BandBackupSection({ bandId, bandName }: { bandId: string; bandName: string }) {
  const { t } = useTranslation();
  // A2 (2026-09-25): RESTORE is a DESTRUCTIVE authority write
  // (`POST /api/bands/:id/restore`, mounted AFTER the server's global read-only
  // guard (server/index.js:519-525; the restore mount itself is at :554) → 403
  // for a read-only account, no capability
  // exempts it) → plain `canWrite`, like the Band Settings page that hosts it.
  // The DOWNLOAD stays open: it is a GET behind `requireBandAccess('editor')`
  // and a frozen account may legitimately export its backup.
  const writeGate = useWriteGate(bandId);
  const canWrite = writeGate.canWrite;
  const restoreInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'backup' | 'restore' | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    setBusy('backup');
    setMessage(null);
    setError(null);
    try {
      const blob = await apiGetBandBackup(bandId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      a.href = url;
      a.download = `bandz-backup-${bandName.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'band'}-${stamp}.bandz.enc`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setMessage(t('bandBackup.downloaded'));
    } catch (e: any) {
      setError(t('bandBackup.backupFailed', { msg: e?.message || '' }));
    } finally {
      setBusy(null);
    }
  };

  const handlePickRestoreFile = async (file: File) => {
    if (!file) return;
    if (!canWrite) return; // before the confirm dialog AND the API call
    setBusy('restore');
    setMessage(null);
    setError(null);
    try {
      // The backup is now ENCRYPTED (.bandz.enc) — send the raw bytes; the
      // server decrypts with the master key, validates and restores.
      const ok = await showConfirm(t('bandBackup.confirm', { name: bandName }), {
        title: t('bandBackup.confirmTitle'),
        confirmLabel: t('bandBackup.confirmBtn'),
        cancelLabel: t('bandBackup.cancel'),
        danger: true,
      });
      if (!ok) return; // user cancelled
      const bytes = await file.arrayBuffer();
      await apiRestoreBand(bandId, bytes);
      setMessage(t('bandBackup.restoreSuccess'));
    } catch (e: any) {
      setError(t('bandBackup.restoreFailed', { msg: e?.message || '' }));
    } finally {
      setBusy(null);
      if (restoreInputRef.current) restoreInputRef.current.value = '';
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 8 }}>
        <button
          className="btn btn-secondary"
          onClick={() => void handleDownload()}
          disabled={busy !== null}
        >
          {busy === 'backup' ? t('bandBackup.downloading') : t('bandBackup.downloadBtn')}
        </button>

        <div>
          <input
            ref={restoreInputRef}
            type="file"
            accept=".enc,.bandz.enc,application/octet-stream"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void handlePickRestoreFile(f); }}
          />
          <button
            className="btn btn-danger"
            onClick={() => restoreInputRef.current?.click()}
            disabled={busy !== null || !canWrite}
          >
            {busy === 'restore' ? t('bandBackup.restoring') : t('bandBackup.restoreBtn')}
          </button>
        </div>
      </div>

      <p className="text-dim" style={{ fontSize: 'var(--font-xs)', marginTop: 0 }}>
        {t('bandBackup.restoreHint')}
      </p>

      {message && <p style={{ color: 'var(--success)' }}>{message}</p>}
      {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
    </div>
  );
}
